
import { Vue, Component, Prop, Watch, Ref } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { isPizzabotIntegration } from '@/utils/helpers';
import { getRestaurantMetaInfo } from '@/utils/meta';
import debounce from 'lodash/debounce';
import Cookie from 'js-cookie';
import Navbar from '@/components/navigation/Navbar.vue';
import MenuBar from '@/components/navigation/MenuBar.vue';
import Modal from '@/components/shared/Modal.vue';
import GenericTableLocationsModal from '@/components/shared/GenericTableLocationsModal.vue';
import MenuGroupResults from '@/components/MenuGroupResults.vue';
import MenuResults from '@/components/MenuResults.vue';
import RotationalMenuResults from '@/components/RotationalMenuResults.vue';
import Filters from '@/components/filter/Filters.vue';
import BannerContainer from '@/components/shared/BannerContainer.vue';
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue';
import AnnouncementModal from '@/components/menu/AnnouncementModal.vue';
import SelectStudentModal from '@/components/menu/SelectStudentModal.vue';
import WarningModal from '@/components/shared/WarningModal.vue';
import CateringBotModal from '@/components/menu/cateringBot/CateringBotModal.vue';
import ProudPartnerOfApp8En from '../../assets/images/proud-partner-of-app8-en.svg?inline';
import ProudPartnerOfApp8Fr from '../../assets/images/proud-partner-of-app8-fr.svg?inline';

const namespace: string = 'cart';

@Component<MenuPage>({
	components: {
		CateringBotModal,
		AnnouncementModal,
		SelectStudentModal,
		MenuBar,
		Navbar,
		Filters,
		BannerContainer,
		Modal,
		GenericTableLocationsModal,
		MenuGroupResults,
		MenuResults,
		RotationalMenuResults,
		LoadingSpinner,
		WarningModal,
		ProudPartnerOfApp8En,
		ProudPartnerOfApp8Fr
	},
	metaInfo() {
		return getRestaurantMetaInfo(this.restaurant);
	}
})
export default class MenuPage extends Vue {
	@Prop({ type: Boolean, required: true, default: '' }) private dataFetched!: boolean;
	@Prop({ type: Boolean, required: true, default: '' }) private cssLoaded!: boolean;

	// Cart
	@Action('setMembershipBanner', { namespace }) private setMembershipBanner!: (show: boolean) => void;
	@Action('getAnotherRound', { namespace }) private getAnotherRound!: (orderId: string) => Promise<boolean>;
	@Action('setCartFromCookies', { namespace }) private setCartFromCookies!: (orderingRequiresLogin: boolean) => Promise<void>;
	@Action('fetchNextOrderQueueTime', { namespace }) private fetchNextOrderQueueTime!: (specifier?: string) => void;
	@Action('verifyConfig', { namespace }) private verifyConfig!: () => Promise<void>;
	@Action('setViewOnly', { namespace }) private setViewOnly!: () => void;
	@Getter('getMenuList', { namespace }) private menuList!: string[] | null;
	@Getter('getMemberId', { namespace }) private memberId!: string | null;
	@Getter('getMemberProvider', { namespace }) private memberProvider!: string | null;
	@Getter('getTableNum', { namespace }) private tableNum!: string | null;
	@Getter('showMembershipBanner', { namespace }) private showMembershipBanner!: boolean;
	@Getter('getContactName', { namespace }) private contactName!: string;
	@Getter('isTakeOut', { namespace }) private isTakeOut!: boolean;
	@Getter('isGenericCatering', { namespace }) private isGenericCatering!: boolean;
	@Getter('isGenericTableLocation', { namespace }) private isGenericTableLocation!: boolean;
	@Getter('isViewOnly', { namespace }) private isViewOnly!: boolean;
	@Getter('getItems', { namespace }) private cartItems!: OrderItem[];
	@Getter('getQueueInfo', { namespace }) private queueInfo!: QueueInfo | null;
	@Getter('getDeliveryInformation', { namespace }) private deliveryInformation!: CheckoutDeliveryInfo | null;

	// Suites
	@Getter('isPreOrdering', { namespace: 'suites' }) private isPreOrdering!: boolean;
	@Getter('isEventDayOrdering', { namespace: 'suites' }) private isEventDayOrdering!: boolean;
	@Getter('getUserEventSuite', { namespace: 'suites' }) private userEventSuite!: UserEvent;

	// Search
	@Getter('getSearchInput', { namespace: 'search' }) private searchText!: string;

	// Restaurant
	@Action('setMemberPrices', { namespace: 'restaurant' }) private setMemberPrices!: (menus: Menu[]) => Promise<Menu[]>;
	@Action('clearMemberPrices', { namespace: 'restaurant' }) private clearMemberPrices!: (menus: Menu[]) => void;
	@Action('setFilteredMenus', { namespace: 'restaurant' }) private setFilteredMenus!: (menus: Menu[]) => Promise<Menu[]>;
	@Action('fetchAnnouncements', { namespace: 'restaurant' }) private fetchAnnouncements!: () => void;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Getter('getRestaurantMembershipCustomizationLoadingMessage', { namespace: 'restaurant' }) private membershipCustomizationLoadingMessage!: string;
	@Getter('getRestaurantMembershipCustomization', { namespace: 'restaurant' }) private membershipCustomization!: MembershipCustomization;
	@Getter('getInitialMenuGroups', { namespace: 'restaurant' }) private initialMenuGroups!: MenuGroup[];
	@Getter('getInitialMenus', { namespace: 'restaurant' }) private initialMenus!: Menu[];
	@Getter('getFilteredMenus', { namespace: 'restaurant' }) private filteredMenus!: Menu[];
	@Getter('getRotationalMenus', { namespace: 'restaurant' }) private rotationalMenus!: RotationalMenu[];
	@Getter('getMemberMenus', { namespace: 'restaurant' }) private memberMenus!: Menu[];
	@Getter('getSupportEmail', { namespace: 'restaurant' }) private supportEmail!: string | null;
	@Getter('isMembershipProgramOn', { namespace: 'restaurant' }) private isMembershipProgramOn!: string[] | string;
	@Getter('isMarketplaceHub', { namespace: 'restaurant' }) private isMarketplaceHub!: boolean;
	@Getter('isK12Location', { namespace: 'restaurant' }) private isK12Location!: boolean;
	@Getter('getGenericTableLocations', { namespace: 'restaurant' }) private genericTableLocations!: GenericTableLocations | null;
	@Getter('orderingRequiresLogin', { namespace: 'restaurant' }) private orderingRequiresLogin!: boolean;
	@Getter('getAnnouncements', { namespace: 'restaurant' }) private announcements!: Announcement[];
	@Getter('isRallyIntelligenceOn', { namespace: 'restaurant' }) private isRallyIntelligenceOn!: boolean;

	// Catering Bot
	@Action('fetchAvailability', { namespace: 'cateringBot' }) private fetchCateringBotAvailability!: () => Promise<void>;

	// Auth
	@Action('removeWelcomeBanner', { namespace: 'auth' }) private removeWelcomeBanner!: () => void;
	@Getter('getFullName', { namespace: 'auth' }) private fullName!: string;
	@Getter('showWelcomeBanner', { namespace: 'auth' }) private showWelcomeBanner!: boolean;
	@Getter('showLoginAsBanner', { namespace: 'auth' }) private showLoginAsBanner!: boolean;
	@Getter('isAuthenticated', { namespace: 'auth' }) private isAuthenticated!: boolean;
	@Getter('isSuiteOperatorLogin', { namespace: 'auth' }) private isSuiteOperatorLogin!: boolean;

	@Ref('nav') readonly navRef!: InstanceType<typeof Navbar>;
	@Ref('menuBar') readonly menuBarRef!: InstanceType<typeof MenuBar>;
	@Ref('banners') readonly bannersRef!: InstanceType<typeof BannerContainer>;
	@Watch('busy')
	onBusyUpdate() {
		// Check nav height
		this.$nextTick(() => {
			this.checkNavHeight();
			this.setupNavResizeObserver();
		});
	}
	private busy: boolean = true;
	private filterOpen: boolean = false;
	private orderPaused: boolean = false;
	private orderPausedModalOpened: boolean = false;
	private reorderChangeModalOpened: boolean = false;
	private viewOnlyModalOpened: boolean = false;
	private membershipAppliedSuccessfully: boolean = false;
	private openCartModal: boolean = false;
	private isAnotherRound: boolean = false;
	private maintenanceOnGoing: boolean = false;
	private showAnnouncements: boolean = false;
	private showSelectStudentModal: boolean = false;
	private isStudentModalEditMode: boolean = false;
	private isCateringBotModalOpen: boolean = false;
	private menus: Menu[] = [];
	private unseenAnnouncements: Announcement[] = [];
	private elementSelected: string = '';
	private currentElementIndex: number = 0;
	private navHeight: number = 0;
	private navbarOnlyHeight: number = 0;
	private navBannerHeight: number = 0;
	private screenWidth: number = window.innerWidth;
	private debouncedResize!: ReturnType<typeof debounce>;
	private navResizeObserver: ResizeObserver | null = null;

	private get hasMembershipInfo(): boolean {
		return !!(this.memberId && this.isMembershipProgramOn && this.membershipCustomization && this.membershipCustomization.banner_message);
	}

	/**
	 * Check if this is a K12 location that requires student selection on load
	 * and user is authenticated
	 *
	 * @return {boolean}
	 */
	private get isK12WithRequiredStudentSelection(): boolean {
		return !!(this.restaurant.k12 && this.restaurant.k12RequiresStudentSelectionOnLoad && this.isAuthenticated);
	}

	/**
	 * Show student indicator if:
	 * - K12 location with student selection on load enabled
	 * - User is authenticated
	 * - Student has been selected in delivery information
	 *
	 * @return {boolean}
	 */
	private get showStudentIndicator(): boolean {
		return !!(this.isK12WithRequiredStudentSelection && this.deliveryInformation?.student);
	}

	private get menuBanners(): BannerItem[] {
		const banners: BannerItem[] = [
			{
				name: 'orders-paused',
				type: 'warning',
				show: this.restaurant.orders_paused,
				message: this.$t('menu.error_orders_paused_banner')
			},
			{
				name: 'view-only',
				type: 'info',
				show: this.isViewOnly,
				message: this.$t('utils.error_validation_dialog.menu_view_only')
			},
			{
				name: 'membership',
				type: 'info',
				show: !!(this.hasMembershipInfo && this.showMembershipBanner),
				message: `${this.$t('menu.membership_drawer.spinner.welcome_back_membership')}${this.contactName ? ` ${this.contactName}` : ''}, ${this.membershipCustomization && this.membershipCustomization.banner_message}`,
				closeOnSwipe: true,
				action: {
					text: this.$t('menu.membership_drawer.btn_dismiss'),
					onClick: () => this.setMembershipBanner(false)
				}
			},
			{
				name: 'login-as',
				type: 'info',
				show: !!(this.showLoginAsBanner && this.fullName),
				message: this.showLoginAsBanner ? this.$t('menu.logged_in_as_banner') : this.$t('menu.welcome_back_banner'),
				action: {
					text: this.fullName,
					onClick: () => this.$router.push({ path: '/profile', query: this.$route.query, hash: this.$route.hash }).catch(() => {})
				}
			},
			{
				name: 'holiday',
				type: 'info',
				show: this.isHoliday && !this.isPreOrdering && !this.isGenericCatering && !this.isK12Location,
				message: this.isTakeOut ? this.$t('menu.location_closed_banner_takeout') : this.$t('menu.location_closed_banner_dinein')
			},
			{
				name: 'welcome',
				type: 'info',
				show: !!(this.showWelcomeBanner && this.fullName && !this.showMembershipBanner && !this.hasMembershipInfo),
				duration: 10000,
				message: this.showWelcomeBanner ? this.$t('menu.welcome_back_banner') : '',
				action: {
					text: this.fullName,
					onClick: () => this.$router.push({ path: '/profile', query: this.$route.query, hash: this.$route.hash }).catch(() => {})
				}
			},
			{
				name: 'queue-info',
				type: 'info',
				show: !!this.queueInfo,
				message: this.$tc('menu.queue_banner', this.queueInfo?.queueSize, {
					count: this.queueInfo?.queueSize,
					minutes: this.queueInfo?.minutesLeft
				})
			}
		];

		return banners;
	}

	private get loadingMessage(): string {
		if (this.isMembershipProgramOn && this.memberId) {
			return `${this.$t('menu.membership_drawer.spinner.welcome_back_membership')} ${this.contactName ? this.contactName : ''}, ${this.membershipCustomizationLoadingMessage}`;
		}
		else if (this.contactName) {
			return this.$t('menu.membership_drawer.spinner.welcome_back_name', { contactName: this.contactName });
		}
		else {
			return this.$t('menu.membership_drawer.spinner.welcome_back_general');
		}
	}

	/**
	 * Check if today is a holiday
	 * Will need to be updated to check for holiday hours in the future
	 *
	 * @return {boolean}
	 */
	private get isHoliday(): boolean {
		const today = DateTime.now().setZone(this.restaurant.zone || 'America/Toronto');
		const holidays = this.restaurant.holiday_hours;
		if (holidays && holidays.length) {
			return holidays.some((holiday: HolidayHours) => {
				return DateTime.fromISO(holiday.date).hasSame(today, 'day');
			});
		}
		return false;
	}

	/**
	 * Fetch the menus and filter them by types depending on query params.
	 * We also check if orders are paused, selected menu, etc. We do not trigger
	 * this if the menus are already fetched.
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		// TEMPORARY HARDCODED FOR BOWLERO.
		// TODO: Remove the restaurant.group check when all Bowlero locations are ready to use restaurant.groups
		if(this.restaurant?.orders_paused && (this.restaurant?.group === 'bowlero' || this.restaurant?.groups?.includes('bowlero'))) {
			this.$router.push({ path: '/bowlero-coming-soon' }).catch(() => {});
		}

		// Show the modal warning of changes to the items when reordering from a past receipt
		if(this.$route.query.openCart === 'true' && this.$route.query.reorderWarning === 'true') {
			this.reorderChangeModalOpened = true;
			let query = { ...this.$route.query };
			delete query.reorderWarning;
			this.$router.replace({ query }).catch(() => {});
		}

		// First load
		if(!this.filteredMenus || !this.filteredMenus.length) {
			try {
				// Get local menus and save the inital menus globally
				this.menus = this.initialMenus;
				// Display correct menus (takeout menus if takeout/generic catering, dinein menus if dinein)
				// and hide/show member menus. Also check if there is a menuList passed in the query params
				await this.findRespectiveMenus();

				// If no menus of a certain type, show an error modal
				// Exclude K12 locations from this check because they don't use normal menus
				if(!this.menus.length && !this.restaurant?.orders_paused && !this.restaurant.k12) {
					this.orderPaused = true;
					this.orderPausedModalOpened = true;
				}
				setTimeout(async () => {
					this.busy = false;
					await this.handleAnnouncements();
				}, 150);
			}
			catch (err) {
				throw err;
			}
		}

		// Subsequent load
		else {
			// Show member menus with discounted prices and special menus
			if(this.memberMenus && this.memberMenus.length) {
				this.menus = this.memberMenus;
				this.setSelectedElement(true);
			}

			// Show filtered menus and make sure to filter out any member only menus
			else {
				this.menus = this.filteredMenus.filter((menu: Menu) => {
					return !menu.member_only;
				});
				this.setSelectedElement(false);
			}
			setTimeout(() => {
				this.busy = false;
			}, 100);
		}

		// Can't pre-order as a guest
		if((this.isSuiteOperatorLogin || this.isPreOrdering) && !this.isAuthenticated) {
			this.$router.push({ path: '/login', query: this.$route.query, hash: this.$route.hash }).catch(() => {});
		}

		// If game day ordering, tab limit must be set
		if(this.isEventDayOrdering && this.userEventSuite && !this.userEventSuite?.tab_limit) {
			this.$router.push({ path: `${this.restaurant.slug}/suite-settings`, query: this.$route.query, hash: this.$route.hash }).catch(() => {});
		}

		// Check to run the another round logic
		if(this.restaurant?.another_round && this.$route.query.anotherRoundId) {
			await this.anotherRoundHandler();
		}

		// Populate order queue time for pizzabot locations
		if(isPizzabotIntegration(this.restaurant?.integrationType)) {
			await this.fetchNextOrderQueueTime();
		}

		// Check if the menu is view only
		// Skip for generic table locations since config check will always fail here
		// This is because the tableNum isn't set until the user selects their location
		if (!this.isGenericTableLocation) {
			try {
				this.verifyConfig();
			}
			catch (error) {
				this.setViewOnly();
				this.viewOnlyModalOpened = true;
			}
		}

		// Check if there's a student in session storage from redirect
		this.checkForRedirectedStudent();

		// Show student selection modal if required for K12 locations
		// Only show if delivery information is not already filled
		if (this.isK12WithRequiredStudentSelection && !this.deliveryInformation?.student) {
			this.showSelectStudentModal = true;
		}
	}

	/**
	 * Add resize event listener
	 *
	 * @return {void}
	 */
	private mounted(): void {
		this.debouncedResize = debounce(() => {
			this.checkNavHeight();
			this.screenWidth = window.innerWidth;
		}, 100);
		window.addEventListener('resize', this.debouncedResize);

		// Hide the membership drawer when coming back to the page if the user
		// already enrolled
		if(this.isMembershipProgramOn && this.memberId) {
			this.membershipAppliedSuccessfully = true;
		}

		if (this.isGenericCatering && this.isRallyIntelligenceOn) {
			this.fetchCateringBotAvailability();
		}
	}

	/**
	 * Remove toasts when leaving page and remove resize event listener
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		this.removeWelcomeBanner();
		this.$toasted.clear();
		this.debouncedResize?.cancel();
		window.removeEventListener('resize', this.debouncedResize);
		this.navResizeObserver?.disconnect();
		this.navResizeObserver = null;
	}

	/**
	 * Display menus depending on the type passed in the URL. After those menus
	 * are parsed we also show/hide the member menus and apply the discount if the
	 * user is a member.
	 *
	 * @param {Restaurant} restaurant
	 * @return {Promise<void>}
	 */
	private async findRespectiveMenus(): Promise<void> {
		// Show only takeout menus (generic catering acts as takeout as well)
		if(this.$route.query.takeout || this.$route.query.catering) {
			this.menus = this.menus.filter((menu: Menu) => {
				return menu.on_demand || menu.scheduled;
			});
		}

		// Show only dine-in menus
		else if(this.$route.query.tableNum) {
			this.menus = this.menus.filter((menu: Menu) => {
				return menu.dine_in;
			});
		}

		// If the orders are paused
		if(this.restaurant.orders_paused && this.restaurant.group !== 'bowlero' && !this.restaurant.groups?.includes('bowlero')) {
			this.orderPaused = true;
			this.orderPausedModalOpened = true;
		}

		// Apply menu list if any
		this.setQueryParamsMenuList();

		// Final fitered menus by all settings (order type, member, etc)
		this.setFilteredMenus(this.menus);

		// Apply membership discount and display member menus if user is member
		await this.checkAndApplyMembership(true);

		this.setCartFromCookies(this.orderingRequiresLogin);
	}

	/**
	 * Check height of the navbar and update if there is a change
	 *
	 * @return {void}
	 */
	private checkNavHeight(): void {
		const navH = this.navRef?.$el?.clientHeight || 0;
		const bannerH = this.bannersRef?.$el?.clientHeight || 0;
		const menuBarH = this.menuBarRef?.$el?.clientHeight || 0;
		const totalHeight = navH + bannerH + menuBarH;
		if (totalHeight !== this.navHeight || navH !== this.navbarOnlyHeight) {
			this.navbarOnlyHeight = navH;
			this.navBannerHeight = navH + bannerH;
			this.navHeight = totalHeight;
		}
	}

	/**
	 * Add an observer that checks for navbar size changes.
	 * E.g. the location's image or font loading slowly.
	 *
	 * @return {void}
	 */
	private setupNavResizeObserver(): void {
		if (this.navResizeObserver || !this.navRef?.$el) {
			return;
		}
		this.navResizeObserver = new ResizeObserver(() => {
			this.debouncedResize();
		});
		this.navResizeObserver.observe(this.navRef.$el);
	}

	/**
	 * Check and apply membership if the user is a member. This function runs on
	 * page load because from the cookies we save we can remember that the user
	 * was in fact a member and won't ask them to enter their phone number again.
	 * This function also runs when a new member does sign up with their phone number.
	 *
	 * @param {boolean} onLoad
	 * @return {void}
	 */
	private async checkAndApplyMembership(onLoad: boolean): Promise<void> {
		// If we're on the first page load, the spinner is already spinning
		if(!onLoad) {
			this.busy = true;
		}

		// Apply membership privileges if applicable
		if (this.isMembershipProgramOn && this.memberId) {
			const accessToMemberOnlyMenus: boolean = this.memberProvider !== 'SPH' && this.memberProvider !== 'Punchh';
			const accessToMemberPricing: boolean = this.memberProvider === 'ELB' || this.memberProvider === 'SPH';

			// If the user is a member, we want to show the member menus
			if (accessToMemberOnlyMenus) {
				this.menus = this.filteredMenus;
			}

			// The user has access to member pricing, we want to apply the member prices
			if (accessToMemberPricing) {
				this.menus = await this.setMemberPrices(this.menus);
			}
			else if (this.memberMenus.length) {
				this.clearMemberPrices(this.menus);
			}

			// Set selected menu depending on hash or if the special menu is there
			this.setSelectedElement(accessToMemberOnlyMenus);

			this.membershipAppliedSuccessfully = true;
			this.setMembershipBanner(true);
		}

		// If the user is not a member of the program, we don't show memberOnly menus
		else {
			this.menus = this.filteredMenus.filter((menu: Menu) => !menu.member_only);

			// Set selected menu depending on hash
			this.setSelectedElement(false);
		}

		if(!onLoad) {
			// Give some time to membership users to see the custom welcome back message
			setTimeout(() => this.busy = false, 1500);
		}
	}

	/**
	 * Set the correct menu / menu group passed in the URL
	 *
	 * @param {boolean} member
	 * @return {void}
	 */
	private setSelectedElement(member: boolean): void {
		// Menu groups
		if(this.isMarketplaceHub) {
			if(window.location.hash) {
				this.handleQueryHash();
			}
			else {
				this.elementSelected = this.initialMenuGroups[0] ? this.initialMenuGroups[0].name : '';
			}
		}

		// Menus
		else {
			// If the user is a member, we do not care about the hash and simply
			// want to get the user on the special menu
			if(member) {
				const specialMenuIndex = this.menus.findIndex((menu: Menu) => menu.member_only);
				const specialMenu = this.menus[specialMenuIndex];
				if(specialMenu) {
					this.currentElementIndex = specialMenuIndex;
					this.elementSelected = specialMenu.name;
					this.orderPaused = false;
					this.orderPausedModalOpened = false;
					window.location.hash = specialMenu.slug;
				}
			}

			// Check the hash is there is a menu selected
			else if(window.location.hash) {
				this.handleQueryHash();
			}

			// Default to first menu
			else {
				this.elementSelected = this.menus[0] ? this.menus[0].name : '';
			}
		}
	}

	/**
	 * Handle the hash query here. Hack for NFC tags that includes a hash. Because of the way the app works,
	 * if the URL on the NFC contains a hash, it won't be recognized because the query
	 * params are passed after. So here we reverse them if this happens, that way we
	 * can handle the hash to find the correct menu.
	 *
	 * @return {void}
	 */
	private handleQueryHash(): void {
		let slug = window.location.hash.split('#')[1];
		let queryIndex = slug.indexOf('?');
		if (queryIndex !== -1) {
			let query = slug.substring(queryIndex);
			slug = slug.substring(0, queryIndex);
			location.href = window.location.href.split(/[?#]/)[0].replace(/\/$/, '') + query + '#' + slug;
		}

		this.setSelectedMenu(slug);
	}

	/**
	 * Set the selected menu from the URL hash, if the slug is null or the slug
	 * does not exists at all, we default to the first menu.
	 *
	 * @param {string} slug
	 * @return {void}
	 */
	private setSelectedMenu(slug: string | null): void {
		// Set the selected menu
		if (slug && this.initialMenuGroups) {
			if(this.isMarketplaceHub) {
				if(this.initialMenuGroups) {
					const menuGroupIdx = this.initialMenuGroups.findIndex((m: MenuGroup) => m.slug === slug);
					const menuGroup = this.initialMenuGroups.find((m: MenuGroup) => m.slug === slug);
					if (menuGroup) {
						this.currentElementIndex = menuGroupIdx;
						this.elementSelected = menuGroup.name;
					}
				}
			}
			else {
				if(this.menus) {
					const menuIdx = this.menus.findIndex((m: Menu) => m.slug === slug);
					const menu = this.menus.find((m: Menu) => m.slug === slug);
					if (menu) {
						this.currentElementIndex = menuIdx;
						this.elementSelected = menu.name;
					}
				}
			}
		}
	}

	/**
	 * Recalculate the height of the navbar when a banner is removed
	 * Called after the banner's fade transition completes (via after-leave)
	 *
	 * @return {void}
	 */
	private handleBannerRemoved(): void {
		this.$nextTick(() => {
			this.checkNavHeight();
		});
	}

	/**
	 * Set menu list from the query params if any is passed. We also concat the member menus
	 * since those ones take priority over anything.
	 *
	 * NOTE: This kinda works fine with the menu groups, but it's not perfect, since the other
	 * menu groups would still show up anyway. Leaving this as is for now, but if the menuList
	 * query param is ever planned to be used with a marketplace location we'll have to keep
	 * this in mind.
	 *
	 * @return {void}
	 */
	private setQueryParamsMenuList(): void {
		if(this.menuList && this.menuList.length) {
			let menuListToShow: Menu[] = [];
			this.menuList.forEach((menuListed: string) => {
				const menuFound: Menu | undefined = this.menus.find((menu: Menu) => !menu.member_only && menu.slug === menuListed);
				if(menuFound) {
					menuListToShow.push(menuFound);
				}
			});

			// We make sure that the menus passed in the list were correct menus, if they were, we override the menus
			// with those ones
			if(menuListToShow && menuListToShow.length) {
				// We want to keep the member menus since those will override the menuList
				const memberMenus: Menu[] = this.menus.filter((menu: Menu) => {
					return menu.member_only;
				});
				// Concat any member menus
				this.menus = menuListToShow.concat(memberMenus);
			}
		}
	}

	/**
	 * Open filter panel as a modal.
	 *
	 * @return {void}
	 */
	private openFilterSelector(): void {
		this.filterOpen = true;
		scrollY = window.pageYOffset;
		document.documentElement.classList.add('modal-open');
		document.body.classList.add('modal-open');
		document.getElementById('app')!.style.top = `-${scrollY.toString()}px`;
	}

	/**
	 * Close filter panel.
	 *
	 * @return {void}
	 */
	private closeFilterSelector(): void {
		this.filterOpen = false;
		document.documentElement.classList.remove('modal-open');
		document.body.classList.remove('modal-open');
		document.getElementById('app')!.style.top = '';
		window.scrollTo({ top: scrollY });
	}

	/**
	 * Change view to the menu / menu group selected
	 *
	 * @param {number} idx
	 * @param {string} elementName
	 * @return {void}
	 */
	private selectElement(idx: number, elementName: string): void {
		if (!this.searchText){
			this.currentElementIndex = idx;
			this.elementSelected = elementName;
			window.scroll(0, 0);
		}
	}

	/**
	 * If enabled in the restaurant, fetch the alcoholic items for another round and fill the cart with them.
	 * If items are added, opens the cart modal.
	 *
	 * @return {Promise<void>}
	 */
	private async anotherRoundHandler(): Promise<void> {
		this.isAnotherRound = true;
		try {
			const changeWarning: boolean = await this.getAnotherRound(this.$route.query.anotherRoundId as string);

			if (this.cartItems.length) {
				if (changeWarning) {
					this.reorderChangeModalOpened = true;
				}
				if (!this.genericTableLocations || !this.isGenericTableLocation) {
					this.openCartModal = true;
				}
			}
			else {
				this.$toasted.show(this.$t('checkout.cart.another_round.no_items'), { type: 'error', position: 'top-center' }).goAway(5000);
			}
		}
		catch {
			this.$toasted.show(this.$t('checkout.cart.another_round.error_fetch'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			let query = { ...this.$route.query };
			delete query.anotherRoundId;
			this.$router.replace({ query }).catch(() => {});
		}
	}

	/**
	 * Fetch the announcements and check if there are any new announcements
	 *
	 * @return {Promise<void>}
	 */
	private async handleAnnouncements(): Promise<void> {
		try {
			await this.fetchAnnouncements();

			if (this.announcements.length) {
				const seenAnnouncements = Cookie.get('seenAnnouncements');

				// If the user has seen announcements before, we check if there are new announcements, otherwise we show all announcements
				if (seenAnnouncements) {
					const parsedCookie: SeenAnnouncements[] = JSON.parse(seenAnnouncements);

					this.announcements.forEach((announcement: Announcement) => {
						// Check if the announcement has been seen before at the current location (in the cookie)
						const seenAnnouncement = parsedCookie.find((cookie: SeenAnnouncements) => cookie.id === announcement.id && cookie.restaurantId === this.restaurant.id);

						// If the announcement is new or has been updated since the last time it was seen, add it to the unseen announcements (currently we don't support updating announcements so this is just a future proofing)
						if (!seenAnnouncement || DateTime.fromISO(announcement.updated_at) > DateTime.fromISO(seenAnnouncement.updated_at)) {
							this.unseenAnnouncements.push(announcement);
						}
					});
				}
				else {
					this.unseenAnnouncements = this.announcements;
				}

				// If there are unseen announcements, open announcements modal
				this.showAnnouncements = !!this.unseenAnnouncements.length;
			}
		}
		catch (error) {
			// Fail silently if we can't fetch announcements
		}
	}

	/**
	 * Check if there's a student in session storage from a redirect
	 * If found, populate the checkout delivery information and remove from session
	 *
	 * @return {void}
	 */
	private checkForRedirectedStudent(): void {
		const storedStudent = sessionStorage.getItem('selectedStudentForDelivery');
		if (storedStudent) {
			try {
				const student: UserStudent = JSON.parse(storedStudent);

				// Fill checkout delivery info
				const deliveryInfo: CheckoutDeliveryInfo = {
					type: 'school',
					name: student.name,
					student: student.name,
					school: student.school,
					grade: student.grade,
					classroom: student.classroom,
					teacher: student.teacher,
					extra: student.extra
				};

				this.$store.dispatch('cart/updateDeliveryInformation', deliveryInfo);

				// Clean up session storage
				sessionStorage.removeItem('selectedStudentForDelivery');
			}
			catch (error) {
				// Fail silently if there's an error parsing the student data
				sessionStorage.removeItem('selectedStudentForDelivery');
			}
		}
	}

	/**
	 * Open student modal in edit mode
	 *
	 * @return {void}
	 */
	private openStudentEditModal(): void {
		this.isStudentModalEditMode = true;
		this.showSelectStudentModal = true;
	}

	/**
	 * Close student modal and reset edit mode
	 *
	 * @return {void}
	 */
	private closeStudentModal(): void {
		this.showSelectStudentModal = false;
		this.isStudentModalEditMode = false;
	}

	/**
	 * Handle order completion from checkout
	 * Re-open student modal for K12 locations that require student selection
	 *
	 * @return {void}
	 */
	private handleOrderCompleted(): void {
		if (this.isK12WithRequiredStudentSelection) {
			this.showSelectStudentModal = true;
			this.isStudentModalEditMode = false;
		}
	}
}
